/**
 * WeeklyPlanPanel.jsx — iter992
 *
 * 7-day habit tracker derived from the user's top-N BTRE priorities.
 * Rows = tasks (auto-generated from priority drivers), cols = Mon..Sun.
 * Click a cell to toggle a task's completion; streak counter animates
 * on every check-in.
 *
 * Backend:
 *   GET  /api/bhi/btre/weekly-plan
 *   POST /api/bhi/btre/weekly-plan/toggle
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Flame, Check, X, Info, RefreshCw, Sparkles,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { toneCls } from "@/utils/pathwayMetaphors";

export default function WeeklyPlanPanel() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/bhi/btre/weekly-plan`);
      setData(d);
    } catch {
      toast.error("Грешка при зареждане на седмичния план.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  // Fast lookup: {task_id: {date: bool}}
  const checkinMap = useMemo(() => {
    const map = {};
    for (const c of (data?.checkins || [])) {
      if (!map[c.task_id]) map[c.task_id] = {};
      map[c.task_id][c.date] = !!c.done;
    }
    return map;
  }, [data]);

  const isDone = (tid, date) => !!checkinMap[tid]?.[date];

  const toggle = async (task_id, date) => {
    const next = !isDone(task_id, date);
    // Optimistic update
    setData((d) => {
      if (!d) return d;
      const others = (d.checkins || []).filter(
        (c) => !(c.task_id === task_id && c.date === date),
      );
      return { ...d, checkins: [...others, { task_id, date, done: next, note: "" }] };
    });
    try {
      await axios.post(`${API}/bhi/btre/weekly-plan/toggle`, {
        task_id, date, done: next,
      });
      // Refresh streak stats
      load();
    } catch {
      toast.error("Грешка при запис. Опитай отново.");
      load();
    }
  };

  const cardCls = isDark ? "bg-slate-900/60 border-slate-700" : "bg-white border-slate-200";
  const mutedCls = isDark ? "text-slate-400" : "text-slate-500";

  if (loading) {
    return (
      <div className={`${cardCls} border rounded-2xl p-6 text-center`}>
        <Loader2 className="w-6 h-6 mx-auto animate-spin opacity-50" />
        <div className={`mt-2 text-sm ${mutedCls}`}>Зареждане на седмичния план…</div>
      </div>
    );
  }

  if (!data || !data.tasks?.length) {
    return (
      <div className={`${cardCls} border rounded-2xl p-6 text-center`} data-testid="weekly-plan-empty">
        <Sparkles className="w-8 h-8 mx-auto opacity-30 mb-2" />
        <div className="text-sm font-semibold mb-1">Нямаш активни задачи</div>
        <div className={`text-xs ${mutedCls}`}>
          {`Избери повече фактори в „Твоят път към превенция", за да генерираме персонален план.`}
        </div>
      </div>
    );
  }

  const today = data.today;
  const days = data.days || [];
  const weekdays = data.weekday_labels || [];

  const streak = data.stats?.streak || 0;
  const doneToday = data.stats?.tasks_completed_today || 0;
  const totalTasks = data.stats?.tasks_total || data.tasks.length;

  return (
    <div className={`${cardCls} border rounded-2xl p-4 md:p-5 space-y-4`} data-testid="weekly-plan-panel">
      {/* Header — streak + summary */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base md:text-lg font-bold flex items-center gap-2">
            🗓️ Твоят седмичен план
          </h3>
          <p className={`text-xs md:text-sm mt-0.5 ${mutedCls}`}>
            Изведен автоматично от твоите топ приоритети. Всеки ден — 1 клик за отметка.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className={`px-3 py-1.5 rounded-full flex items-center gap-1.5 ${
            streak >= 3
              ? "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300"
              : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
          }`} data-testid="weekly-plan-streak">
            <Flame className={`w-4 h-4 ${streak >= 3 ? "text-orange-500" : "opacity-40"}`} />
            <span className="text-sm font-bold">{streak}</span>
            <span className="text-[11px] opacity-80">
              {streak === 1 ? "ден" : "дни"} серия
            </span>
          </div>
          <button
            type="button"
            onClick={() => { setRefreshing(true); load(); }}
            disabled={refreshing}
            className={`p-1.5 rounded-full transition ${
              isDark ? "hover:bg-slate-700" : "hover:bg-slate-100"
            } disabled:opacity-40`}
            title="Обнови"
            data-testid="weekly-plan-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Today snapshot */}
      <div className={`rounded-lg p-3 text-xs md:text-sm flex items-center gap-2 ${
        doneToday > 0
          ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
          : "bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300"
      }`} data-testid="weekly-plan-today">
        <Info className="w-4 h-4 shrink-0" />
        <span>
          <b>Днес:</b> {doneToday} / {totalTasks} задачи изпълнени
          {streak >= 3 && ` · продължи серията за да я задържиш! 🔥`}
        </span>
      </div>

      {/* Grid */}
      <div className="overflow-x-auto -mx-2 px-2">
        <table className="w-full min-w-[540px] text-sm" data-testid="weekly-plan-grid">
          <thead>
            <tr>
              <th className={`text-left p-2 font-semibold text-[11px] uppercase tracking-wide ${mutedCls}`}>
                Задача
              </th>
              {days.map((d, i) => {
                const isToday = d === today;
                return (
                  <th
                    key={d}
                    className={`p-1 text-center font-semibold text-[11px] ${
                      isToday
                        ? "text-emerald-600 dark:text-emerald-400"
                        : mutedCls
                    }`}
                  >
                    <div>{weekdays[i]}</div>
                    <div className="text-[9px] font-normal opacity-70">
                      {d.slice(8, 10)}
                    </div>
                  </th>
                );
              })}
              <th className={`p-1 text-center text-[11px] font-semibold ${mutedCls}`}>Цел</th>
            </tr>
          </thead>
          <tbody>
            {data.tasks.map((t) => {
              const doneCount = days.reduce((n, d) => n + (isDone(t.task_id, d) ? 1 : 0), 0);
              const hitGoal = doneCount >= t.target_days;
              return (
                <tr
                  key={t.task_id}
                  className={isDark ? "border-t border-slate-800" : "border-t border-slate-100"}
                  data-testid={`weekly-plan-row-${t.factor_id}`}
                >
                  <td className="p-2 align-top">
                    <div className="flex items-start gap-2">
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm shrink-0 ${toneCls(t.tone)}`}>
                        {t.emoji}
                      </span>
                      <div className="min-w-0">
                        <div className="font-semibold text-sm leading-tight">{t.title}</div>
                        <div className={`text-[11px] leading-snug ${mutedCls}`}>{t.verb}</div>
                        <div className={`text-[10px] italic mt-0.5 ${
                          isDark ? "text-slate-500" : "text-slate-400"
                        }`}
                        title={t.why}>
                          {t.factor_name}
                        </div>
                      </div>
                    </div>
                  </td>
                  {days.map((d) => {
                    const done = isDone(t.task_id, d);
                    const isToday = d === today;
                    const isPast = d < today;
                    return (
                      <td key={d} className="p-1 text-center">
                        <button
                          type="button"
                          onClick={() => toggle(t.task_id, d)}
                          className={`w-8 h-8 rounded-lg transition-all flex items-center justify-center ${
                            done
                              ? "bg-emerald-500 text-white hover:bg-emerald-600 shadow"
                              : isToday
                              ? "border-2 border-emerald-400 dark:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-500/10"
                              : isPast
                              ? "border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 opacity-60"
                              : "border border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 opacity-40"
                          }`}
                          title={`${t.title} · ${d}`}
                          data-testid={`weekly-plan-cell-${t.factor_id}-${d}`}
                          aria-label={`Отметни ${t.title} за ${d}`}
                        >
                          {done ? <Check className="w-4 h-4" /> : null}
                        </button>
                      </td>
                    );
                  })}
                  <td className="p-1 text-center">
                    <span
                      className={`text-[11px] font-mono px-1.5 py-0.5 rounded ${
                        hitGoal
                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                          : isDark
                          ? "bg-slate-800 text-slate-300"
                          : "bg-slate-100 text-slate-600"
                      }`}
                      title="Изпълнени / целеви дни"
                    >
                      {doneCount}/{t.target_days}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className={`text-[10px] italic ${mutedCls}`}>
        {`Планът се обновява автоматично, когато промениш избора си в „Твоят път към превенция". Серията се пази до 60 дни назад.`}
      </p>
    </div>
  );
}
